import {
  type PropsWithChildren,
  useContext,
  useMemo,
  createContext,
} from 'react';

import type { DataTableV2Config } from '../DataTableV2/public.api.js';
import { deserializeDataTableV2Config } from '../utils/datatable-config-utils.js';

/**
 * @internal
 */
const DataTableV2ConfigContext = createContext<DataTableV2Config>({});

/**
 * Provider for the DataTableV2 API config
 * @public
 */
export function DataTableV2ConfigProvider(
  props: PropsWithChildren<{ value: DataTableV2Config | string }>,
) {
  const { children, value } = props;

  const deserializedConfig = useMemo(() => {
    return typeof value === 'string'
      ? deserializeDataTableV2Config(value)
      : value;
  }, [value]);

  return (
    <DataTableV2ConfigContext.Provider value={deserializedConfig}>
      {children}
    </DataTableV2ConfigContext.Provider>
  );
}

/**
 * @internal
 */
export const useDataTableV2Config = () => {
  return useContext(DataTableV2ConfigContext);
};

/**
 * Internal helper function that returns the value of
 * `DataTableV2`'s config options based on the `key`.
 * @internal
 */
export function useDataTableV2ConfigElement<T extends keyof DataTableV2Config>(
  key: T,
): DataTableV2Config[T] | undefined {
  const contextConfig = useContext(DataTableV2ConfigContext);

  if (
    typeof contextConfig?.[key] === 'undefined' ||
    contextConfig?.[key] === null
  ) {
    return undefined;
  }

  return contextConfig[key];
}
